import { useCallback, useEffect, useState } from 'react';

type Fetcher<Data, Args extends unknown[]> = (...args: Args) => Promise<Data>;

interface UseFetchOptions {
	enabled?: boolean;
}

interface UseFetchResult<Data> {
	data: Data | null;
	enabled: boolean;
	error: Error | null;
	loading: boolean;
	refetch: () => void;
}

function useFetch<Data, Args extends unknown[]>(
	fetcher: Fetcher<Data, Args>,
	args: Args,
	{ enabled = true }: UseFetchOptions = {},
): UseFetchResult<Data> {
	const [data, setData] = useState<Data | null>(null);
	const [loading, setLoading] = useState<boolean>(enabled);
	const [error, setError] = useState<Error | null>(null);

	const fetchData = useCallback(async () => {
		let isActive = true;

		setData(null);
		setLoading(true);
		setError(null);

		try {
			const result = await fetcher(...args);
			if (isActive) {
				setData(result);
			}
		} catch (err) {
			if (isActive) {
				setError(err instanceof Error ? err : new Error('Unknown error'));
			}
		} finally {
			if (isActive) {
				setLoading(false);
			}
		}

		return () => {
			isActive = false;
		};
		// eslint says "React Hook useEffect has a missing dependency: 'args'". We already have spread args in the dependency array.
	}, [fetcher, setLoading, setData, setError, ...args]); // eslint-disable-line react-hooks/exhaustive-deps

	const refetch = useCallback(() => {
		fetchData();
	}, [fetchData]);

	useEffect(() => {
		if (!enabled) {
			setLoading(false);
			setData(null);
			setError(null);
			return;
		}
		fetchData();
	}, [enabled, fetchData, setLoading, setData, setError]);

	return { data, loading, error, enabled, refetch };
}

export default useFetch;
